import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Payment } from "square";
import { getPayment } from "../api";
import CopyButton from "../components/CopyButton";
import TermsAndAgreementsModal from "../components/TermsAndAgreementsModal";
import { OrderItem } from "../types/OrderItem";
import OrderItems from "../components/OrderItems";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import { formatDeliveryEstimate } from "../extensions/helpers";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";

const PaymentReceiptPage: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useCustomerAuth();

  const [receipt, setReceipt] = useState<Payment>(location.state?.receipt);
  const [purchasedCart] = useState<OrderItem[]>(location?.state?.purchasedCart);
  const deliveryEstimate = location?.state?.deliveryEstimate as
    | { minDeliveryDate?: string; maxDeliveryDate?: string }
    | undefined;
  const deliveryEstimateText = formatDeliveryEstimate(
    deliveryEstimate?.minDeliveryDate,
    deliveryEstimate?.maxDeliveryDate,
  );
  const searchPaymentId: string | null = searchParams.get("id");
  // Set only when checkout had to sign the buyer out mid-order (see
  // PaymentConfirm's accountNotice). Absent on a receipt reached by ?id=, which
  // carries no navigation state — that's fine, it's a one-time message about
  // the checkout that just happened, not a property of the order.
  const accountNotice = location?.state?.accountNotice as string | undefined;
  // The address the order's mail was actually sent to. Carried in navigation
  // state from PaymentConfirm, so it's absent on a receipt reached by ?id=
  // (which has no state) — the line below is simply omitted then.
  const confirmationEmail = location?.state?.confirmationEmail as
    | string
    | undefined;

  useEffect(() => {
    const init = async () => {
      try {
        if (searchPaymentId) {
          const searchPaymentResponse = await getPayment(searchPaymentId, {
            authToken: session?.authToken,
          });
          if (
            searchPaymentResponse &&
            !!searchPaymentResponse.data &&
            !searchPaymentResponse.error
          ) {
            setReceipt(searchPaymentResponse.data);
            return;
          }
        }

        // If no receipt from either payment process or from search params go back to home page
        if (!receipt) {
          navigate("/home");
        }
      } catch (err) {
        console.error("Failed to retrieve payment:", err);
        navigate("/home");
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageContainer hasPadding gapSize={GapSize.Tiny}>
      <OrderWrapper>
        {/* Outside .printable on purpose: this is about the buyer's session,
            not the order, and has no business on a printed receipt. */}
        {accountNotice && (
          <div
            className="mb-4 rounded-lg border border-rust bg-cream-light p-3 text-sm text-brown-dark opensans-text"
            role="status"
          >
            {accountNotice}
          </div>
        )}
        {/* Everything inside .printable is what "Print / Save as PDF" outputs —
            the action buttons below sit outside it and are hidden in print. */}
        <div className="printable flex flex-col gap-4">
          {/* Success Banner */}
          <div className="receipt-success-banner">
            <div className="receipt-success-icon-ring">
              <svg
                className="receipt-success-checkmark"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="receipt-success-title">Order Confirmed!</h1>
            <p className="receipt-success-subtitle">
              Thank you for your order — we're working hard to get your
              beautiful new clothes to you shortly.
            </p>
            {/* Name the inbox. Until this line existed nothing in the whole
                flow ever showed the buyer which address their confirmation
                went to, so a mail that landed somewhere unexpected looked
                exactly like a mail that was never sent. */}
            {confirmationEmail && (
              <p className="receipt-success-subtitle">
                A confirmation email is on its way to{" "}
                <strong translate="no">{confirmationEmail}</strong>. If it isn't
                there in a few minutes, check your spam folder or contact{" "}
                <a href="mailto:support@americanaandcompany.com">
                  support@americanaandcompany.com
                </a>
                .
              </p>
            )}
          </div>

          <div className="receipt-divider" />

          {/* Purchased Items */}
          <OrderItems orderItems={purchasedCart} isCheckout />

          <div className="receipt-divider" />

          {/* Receipt Details */}
          <div className="receipt-details">
            <h2 className="receipt-section-title">Receipt</h2>

            <div className="receipt-row">
              <span className="receipt-row-label">Order ID</span>
              <div className="flex items-center gap-2 min-w-0">
                <CopyButton textToCopy={receipt?.orderId ?? ""} />
                <span className="receipt-id-text" translate="no">
                  {receipt?.orderId}
                </span>
              </div>
            </div>

            <div className="receipt-row">
              <span className="receipt-row-label">Amount</span>
              <span className="receipt-amount-value">
                ${(Number(receipt?.amountMoney?.amount) / 100).toFixed(2)}
              </span>
            </div>

            <div className="receipt-row">
              <span className="receipt-row-label">Status</span>
              <span className="receipt-status-badge">{receipt?.status}</span>
            </div>

            <div className="receipt-row">
              <span className="receipt-row-label">Date</span>
              <span className="text-sm text-gray-600">
                {new Date(receipt?.createdAt ?? "").toLocaleString()}
              </span>
            </div>

            {deliveryEstimateText && (
              <div className="receipt-row">
                <span className="receipt-row-label">Estimated Delivery</span>
                <span className="text-sm text-gray-600">
                  {deliveryEstimateText}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* /.printable */}

        <div className="receipt-divider no-print" />

        {/* Actions */}
        <div className="receipt-actions no-print">
          <button
            className="receipt-action-btn receipt-action-btn--secondary"
            onClick={() => window.print()}
          >
            Print / Save as PDF
          </button>
          <button
            className="receipt-action-btn receipt-action-btn--primary"
            onClick={() => {
              navigate("/orders/track", {
                state: { orderId: receipt?.orderId },
              });
            }}
          >
            Track Your Order
          </button>
          <button
            className="receipt-action-btn receipt-action-btn--secondary"
            onClick={() => {
              navigate("/orders/cancel", {
                state: { orderId: receipt?.orderId },
              });
            }}
          >
            Returns &amp; Exchanges
          </button>
          <div className="flex justify-center">
            <TermsAndAgreementsModal />
          </div>
        </div>
      </OrderWrapper>
    </PageContainer>
  );
};

export default PaymentReceiptPage;
